import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'; // Needed for Touch functionality of Material Components
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LayoutModule } from './layout/layout.module';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarConfig } from '@angular/material/snack-bar';
import { AuthenticationService } from './services/base/authentication.service';
import { ApiService } from './services/base/api.service';
import { SnackbarService } from './services/base/snackbar.service';
import { AddTokenInterceptor } from './interceptors/add-token.interceptor';
import { AddAcceptInterceptor } from './interceptors/add-accept.interceptor';
import { AuthorizationService } from './services/base/authorization.service';
import { FormService } from './services/base/form.service';
import { ApiEndpointsService } from './services/base/api-endpoints.service';
import { FiltersService } from './services/base/filters.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ReactiveFormsModule } from '@angular/forms';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorIntlDutch } from '../@fury/shared/internationalization/mat-paginator/mat-paginator-intl.dutch';
import { ReportService } from './services/reports/report.service';
import { ReportPanelService } from './services/reports/report-panel.service';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DatetimeAdapter, MTX_DATETIME_FORMATS, MtxNativeDatetimeModule } from '@ng-matero/extensions/core';
import { MomentDatetimeAdapter, MtxMomentDatetimeModule } from '@ng-matero/extensions-moment-adapter';
import { LightboxModule } from 'ng-gallery/lightbox';
import { PublicKnowledgeBaseService } from './services/knowledge-base/public-knowledge-base.service';
import { FaqService } from './services/knowledge-base/faq.service';
import { AddSocketIdInterceptor } from './interceptors/add-socket-id.interceptor';
import { SupportContactDetailsService } from './services/knowledge-base/support-contact-details.service';
import { LeafletMapService } from './services/base/leaflet-map.service';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular-ivy';
import { FuryAlertBoxModule } from '../@fury/shared/alert-box/alert-box.module';
import { LayoutService } from './layout/layout.service';
import { LocalStorageService } from './services/base/local-storage.service';
import { UserService } from './services/routering/user.service';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { CompanyService } from './services/info/company.service';
import { QuillService } from './services/base/quill.service';

@NgModule({
  imports: [
    // Angular Core Module // Don't remove!
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ReactiveFormsModule,

    // Fury Core Modules
    AppRoutingModule,

    // Layout Module (Sidenav, Toolbar, Quickpanel, Content)
    LayoutModule,
    MtxNativeDatetimeModule,
    MtxMomentDatetimeModule,

    LightboxModule.withConfig({
      panelClass: 'fullscreen'
    }),

    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: true, // environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    FuryAlertBoxModule,

    // Register a Service Worker (optional)
    // ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production })
  ],
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
      },
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: DatetimeAdapter,
      useClass: MomentDatetimeAdapter,
    },
    {
      provide: MTX_DATETIME_FORMATS,
      useValue: {
        parse: {
          dateInput: 'YYYY-MM-DD',
          monthInput: 'MMMM',
          yearInput: 'YYYY',
          timeInput: 'HH:mm',
          datetimeInput: 'YYYY-MM-DD HH:mm',
        },
        display: {
          dateInput: 'DD-MM-YY',
          monthInput: 'MMMM',
          yearInput: 'YYYY',
          timeInput: 'HH:mm',
          datetimeInput: 'DD-MM-YY HH:mm',
          monthYearLabel: 'YYYY MMMM',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
          popupHeaderDateLabel: 'MMM DD, ddd',
        },
      },
    },
    {provide: MAT_DATE_LOCALE, useValue: 'nl-NL'},
    {
      provide: MAT_DATE_FORMATS, useValue: {
        parse: {
          dateInput: 'DD-MM-YY'
        },
        display: {
          dateInput: 'DD-MM-YY',
          monthLabel: 'MMMM',
          monthYearLabel: 'MMMM YYYY',
          dateA11yLabel: 'MMMM',
          monthYearA11yLabel: 'YYYY'
        }
      }
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      }
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline'
      } as MatFormFieldDefaultOptions
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 5000,
        horizontalPosition: 'end',
        verticalPosition: 'bottom'
      } as MatSnackBarConfig
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddTokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddAcceptInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddSocketIdInterceptor,
      multi: true
    },
    {
      provide: MatPaginatorIntl,
      useValue: MatPaginatorIntlDutch()
    },
    ApiEndpointsService,
    ApiService,
    AuthenticationService,
    AuthorizationService,
    FiltersService,
    FormService,

    LayoutService,
    LeafletMapService,
    LocalStorageService,
    SnackbarService,
    UserService,
    FaqService,
    SupportContactDetailsService,
    PublicKnowledgeBaseService,
    ReportService,
    ReportPanelService,
    InfiniteScrollModule,
    CompanyService,
    QuillService,
  ]
})
export class AppModule {
}
